import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router';
import { Loader } from '@f2m3/shared-frontend-ui';
import { useLogin } from '../../hooks/useLogin';
import { useAccountInfo } from '../../api/queryHooks/account';
import { isAdmin, isRetouchStaff, isSalesStaff } from '../../auth/roles';
import type { AccountRole } from '../../api/types';
import StaffLanding from './StaffLanding';

const AdminDashboard = lazy(() => import('./AdminDashboard'));

/**
 * Non-admin staff landing, in spec order (2026-09-14 §4.3): Relations for
 * sales staff when the flag is on; Retouch for retouch staff when the flag
 * is on; the Variants work page for retouch staff; else the landing notice.
 */
const StaffIndex = ({ roles }: { roles: readonly AccountRole[] | undefined }) => {
  const { data, isPending } = useAccountInfo();
  if (isPending) return <Loader />;
  const perms = data?.permissions ?? [];
  if (isSalesStaff(roles) && perms.includes('relations')) return <Navigate to="/admin/relations" replace />;
  if (isRetouchStaff(roles)) {
    if (perms.includes('retouch')) return <Navigate to="/admin/retouch" replace />;
    return <Navigate to="/admin/customizations" replace />;
  }
  return <StaffLanding />;
};

/** /admin index. Admins get the dashboard; other staff go to their feature (StaffIndex). */
const AdminIndex = () => {
  const { userData } = useLogin();
  if (!isAdmin(userData?.roles)) return <StaffIndex roles={userData?.roles} />;
  return (
    <Suspense fallback={<Loader />}>
      <AdminDashboard />
    </Suspense>
  );
};

export default AdminIndex;
